import { Grid, Typography } from '@material-ui/core';
import { useScrollButtonState } from '@ncr-retail/hooks';
import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import BaseButton from 'src/common/components/base-button';
import { TenderType } from 'src/common/enumerations/tender-type';
import { ITender } from 'src/common/interfaces/dtos/ITender';
import { AppState, store } from 'src/common/redux/configure-store';
import { TenderExchangeActions } from 'src/common/redux/reducers/tender-exchange-reducer';
import { trainingModeStyles } from 'src/common/training-mode/training-mode-styles';
import { popupMessage } from 'src/messages/business-logic/popup-message';
import { Pagination } from 'src/pagination/pagination';
import { showSearchTenderVoucherModal } from 'src/tender-voucher/tender-exchange-payment-voucher';
import { codeContainerStyles } from './tender-exchange-styles';
import { ITenderExchangeWindowProps } from './tender-exchange-window';
import showPinpadDisconnectedModal from 'src/common/components/pinpad-disconnected-modal';

const TenderExchange = (props: ITenderExchangeWindowProps): JSX.Element => {
  const { paymentTender, onSubmit, updateTenderExchangeDetails, onBack, onVoucherPayment } = props;
  const isLeftHanded = useSelector<AppState, boolean>(state => state.cashierPreferences.leftHanded);
  const classes = codeContainerStyles({ leftHanded: isLeftHanded });
  const trainingModeClasses = trainingModeStyles();
  const { t } = useTranslation();
  const leftHanded = useSelector((state: AppState): boolean => state.cashierPreferences.leftHanded);
  const trainingMode = useSelector((state: AppState): boolean => state.posMode.trainingMode);
  const hasPinpad = useSelector(
    (state: AppState): boolean => state.pinPad?.isPinpadConfigured || false,
  );
  const [{ currentPage, lastPage, onPageChanged }, scrollRef] = useScrollButtonState();

  const tenderGroup = paymentTender
    ? props.tenderExchange.refundTenders
    : props.tenderExchange.purchaseTenders;

  const onClick = useCallback(
    (tender: ITender): void => {
      if (!hasPinpad && tender.isProcessedByEps) {
        showPinpadDisconnectedModal();
      } else {
        store.dispatch(
          TenderExchangeActions.updateTenderExchangeDetails({ isTenderClicked: true }),
        );
        if (!paymentTender) {
          if (tender.type === TenderType.Voucher) {
            showSearchTenderVoucherModal({ onBack, onVoucherPayment });
          } else {
            updateTenderExchangeDetails({ paymentTender: tender });
          }
        } else {
          updateTenderExchangeDetails({ refundTender: tender });
          onSubmit().catch(popupMessage);
        }
      }
    },
    [updateTenderExchangeDetails, onSubmit, onBack, onVoucherPayment, hasPinpad, paymentTender],
  );

  return (
    <Grid
      item
      className={`fullHeight flexFull ${classes.outerMostContainer}`}
      data-testid='tender-exchange'
    >
      <Grid
        item
        className={`${
          trainingMode ? trainingModeClasses.trainingModeBackground : classes.background
        } fullWidth fullHeight`}
      >
        <Grid
          container
          wrap='nowrap'
          direction={isLeftHanded ? 'row-reverse' : 'row'}
          className={`fullHeight ${classes.outerFlexDesign}`}
          data-testid='tender-exchange-container'
        >
          <Grid
            item
            xs={3}
            container
            className={`${classes.flexDesign} ${classes.tenderExchangeBodyContainer} `}
            direction='column'
            data-testid='tender-exchange-body-container'
          >
            <Grid container item>
              <Grid item xs className={classes.containerHeaderActions}>
                <Typography
                  variant='body1'
                  className={`${classes.buttonOptionsSection} ${classes.containerSubHeaderLabel} `}
                  data-testid={`tender-exchange-first-helper-text`}
                >
                  {t('TenderOptionsHeader')}
                </Typography>
              </Grid>
              <Grid container item className={classes.leftColumn}>
                <Grid
                  container
                  item
                  ref={scrollRef}
                  className={`${classes.verticalListContainer} verticalScroll`}
                >
                  <Grid item className={classes.deviceList}>
                    {tenderGroup
                      .filter(i => i.description)
                      .map(tender => (
                        <BaseButton
                          key={tender.type ?? 'tender'}
                          buttonType='white'
                          onClick={() => onClick(tender)}
                          className={classes.buttonAction}
                          data-testid={`${tender.description}-side-button`}
                        >
                          {tender.description}
                        </BaseButton>
                      ))}
                  </Grid>
                </Grid>
              </Grid>
              <Grid container item className={classes.bottomRow}>
                <Grid container item justifyContent='center' className={classes.leftColumn}>
                  <Grid container item justifyContent='space-evenly' className={classes.pagination}>
                    <Pagination
                      currentPage={currentPage}
                      totalPages={lastPage}
                      updateCurrentPageHandler={onPageChanged}
                      displayText
                      testIdPrefix={'pagination-side-actions'}
                    />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
          <Grid
            item
            container
            className={classes.flexDesign}
            direction={leftHanded ? 'row-reverse' : 'row'}
            data-testid='tender-exchange-body-container'
          >
            <Grid item container className={classes.containerAlignment}>
              <Grid item>
                <Typography
                  className={`${classes.returnItemTitleHeaderInside}`}
                  data-testid={`TenderExchangeMessage1`}
                  variant='h4'
                >
                  {props.paymentTender ? t('RefundTender') : t('TenderMessage1')}
                </Typography>
                <Grid container justifyContent='center'>
                  <Typography
                    className={`${classes.orSection}`}
                    data-testid={`TenderExchangeScanMessage2`}
                    variant='h4'
                  >
                    - {t('BalanceInquiryScanMessage2')} -
                  </Typography>
                </Grid>
                <Typography
                  className={`${classes.returnItemTitleHeaderInside}`}
                  data-testid={`InstructCustomerMessage`}
                  variant='h4'
                >
                  {t('InstructCustomer')}
                </Typography>
                <Typography
                  className={`${classes.returnItemTitleHeaderInside}`}
                  data-testid={`CardAtPinPadMessage`}
                  variant='h4'
                >
                  {t('CardAtPinPad')}
                </Typography>
              </Grid>
            </Grid>
            <Grid item container justifyContent='center' className={classes.selfAlign}>
              <BaseButton
                buttonType='white'
                onClick={props.onCancel}
                data-testid='tender-exchange-cancel-button'
              >
                {t('Cancel')}
              </BaseButton>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};

export default TenderExchange;
